import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { Mail, Lightbulb, Zap, Ban, Sparkles, Printer, Copy, Check, ArrowLeft, Smartphone, Download, MessageCircle, MessageSquare, Share2, QrCode, Compass, Mic, Swords, Sticker } from "lucide-react";
import { CocoonMark } from "@/components/CocoonMark";

const HERO = "https://static.prod-images.emergentagent.com/jobs/08c324fb-59b2-4fff-a725-0bafcdd9ead9/images/a96a3e4bd4c58cebbc68c956ad201b8cbfcf8926175283e044e548ffa5f6c15a.jpeg";
const SITE = "https://www.cocoonmind.com";

// Libellés du générateur de QR d'événement + sticker (4 langues)
const EVT = {
  fr: {
    stickerLabel: "Sticker « die-cut »", stickerLead: "Un autocollant carré à imprimer et à coller partout.",
    stickerDl: "Télécharger le sticker",
    evtTitle: "QR d'événement", evtLead: "Génère un visuel qui ouvre directement une zone, une conférence ou un débat — avec ton parrainage inclus.",
    tabZones: "Zones", tabConfs: "Conférences", tabDebates: "Débats",
    pick: "Choisis un événement à partager :",
    evtDownload: "Télécharger le visuel", evtScan: "Aperçu — scanne pour ouvrir l'événement",
    zones: { eveil: "L'Éveil", atelier: "L'Atelier", exploration: "L'Exploration", bibliotheque: "La Bibliothèque", agora: "L'Agora", adn: "Notre ADN" },
    empty: "Rien à afficher pour le moment.",
  },
  en: {
    stickerLabel: "Die-cut sticker", stickerLead: "A square sticker to print and stick anywhere.",
    stickerDl: "Download the sticker",
    evtTitle: "Event QR", evtLead: "Generate a visual that opens a zone, a talk or a debate directly — with your referral included.",
    tabZones: "Zones", tabConfs: "Talks", tabDebates: "Debates",
    pick: "Pick an event to share:",
    evtDownload: "Download the visual", evtScan: "Preview — scan to open the event",
    zones: { eveil: "The Awakening", atelier: "The Workshop", exploration: "Exploration", bibliotheque: "The Library", agora: "The Agora", adn: "Our DNA" },
    empty: "Nothing to show yet.",
  },
  es: {
    stickerLabel: "Pegatina die-cut", stickerLead: "Una pegatina cuadrada para imprimir y pegar donde quieras.",
    stickerDl: "Descargar la pegatina",
    evtTitle: "QR de evento", evtLead: "Genera un visual que abre directamente una zona, una charla o un debate — con tu código de referido incluido.",
    tabZones: "Zonas", tabConfs: "Charlas", tabDebates: "Debates",
    pick: "Elige un evento para compartir:",
    evtDownload: "Descargar el visual", evtScan: "Vista previa — escanea para abrir el evento",
    zones: { eveil: "El Despertar", atelier: "El Taller", exploration: "La Exploración", bibliotheque: "La Biblioteca", agora: "El Ágora", adn: "Nuestro ADN" },
    empty: "Nada que mostrar por ahora.",
  },
  nl: {
    stickerLabel: "Die-cut sticker", stickerLead: "Een vierkante sticker om te printen en overal te plakken.",
    stickerDl: "Sticker downloaden",
    evtTitle: "Evenement-QR", evtLead: "Genereer een beeld dat direct een zone, lezing of debat opent — met jouw referral erin.",
    tabZones: "Zones", tabConfs: "Lezingen", tabDebates: "Debatten",
    pick: "Kies een evenement om te delen:",
    evtDownload: "Beeld downloaden", evtScan: "Voorbeeld — scan om het evenement te openen",
    zones: { eveil: "Het Ontwaken", atelier: "Het Atelier", exploration: "De Verkenning", bibliotheque: "De Bibliotheek", agora: "De Agora", adn: "Ons DNA" },
    empty: "Nog niets te tonen.",
  },
};
const ZONE_IDS = ["eveil", "atelier", "exploration", "bibliotheque", "agora", "adn"];

const T = {
  fr: {
    eyebrow: "L'Invitation",
    subtitle: "Le précepteur numérique qui réinvente le plaisir d'apprendre.",
    hook: "Envie d'apprendre sans la pression ? Découvre une nouvelle façon de grandir.",
    intro: "Bienvenue dans l'univers COCOON MIND, ton compagnon numérique sur-mesure. Ici, pas de pression, pas de jugement : juste de la curiosité, du jeu et le plaisir de découvrir le monde à ton propre rythme.",
    constatLabel: "Le Constat",
    constat: "Entre le stress des notes, la peur de l'échec et la rigidité du système, l'apprentissage devient souvent une source d'anxiété plutôt qu'un plaisir. L'étincelle naturelle de la curiosité s'éteint sous le poids de la routine scolaire et de la pression quotidienne.",
    solutionLabel: "La Solution : COCOON MIND",
    solution: "COCOON MIND ne vient pas remplacer l'école, mais l'accompagner différemment. Grâce à la ludification — le jeu vidéo au service du savoir — nous transformons l'apprentissage en une aventure passionnante.",
    nots: ["Pas d'école", "Pas de professeurs", "Pas de devoirs", "Pas d'examens", "Pas de diplômes", "Pas de stress"],
    closing: "Simplement toi et moi. Un précepteur virtuel toujours disponible, adapté à ta personnalité, qui transforme chaque question en un jeu et chaque découverte en une victoire.",
    joinLabel: "Rejoins l'aventure",
    joinCta: "Télécharge l'application",
    scan: "Scanne pour découvrir",
    print: "Imprimer / PDF",
    copy: "Copier le message",
    copied: "Message copié !",
    back: "Retour à l'accueil",
    socialTitle: "Carte réseaux sociaux",
    socialLead: "Un visuel carré prêt à poster sur Instagram, TikTok ou en story.",
    download: "Télécharger la carte",
    storyLabel: "Version story (9:16)",
    waLabel: "WhatsApp",
    smsLabel: "SMS",
    shareImg: "Partager l'image",
    msgLabel: "Votre petite phrase (optionnel)",
    msgPlaceholder: "Ex : Je pense que ça va te plaire 🙂",
    msgIdeas: "Idées",
    suggestions: [
      "Je pense que ça va te plaire 🙂",
      "Apprendre sans pression, ça existe — essaie !",
      "J'ai trouvé un truc pour toi, jette un œil 👀",
    ],
    title2: "Ton invitation à imprimer & partager",
    lead: "Un support prêt à l'emploi pour transmettre l'essence de Cocoon Mind à tes proches.",
    share: "✨ Je t'invite à découvrir COCOON MIND — le précepteur numérique qui réinvente le plaisir d'apprendre.\n\nPas d'école, pas de notes, pas de stress : juste de la curiosité, du jeu et le plaisir de découvrir le monde à ton rythme.\n\nRejoins l'aventure 👉 " + SITE,
  },
  en: {
    eyebrow: "The Invitation",
    subtitle: "The digital preceptor reinventing the joy of learning.",
    hook: "Want to learn without the pressure? Discover a new way to grow.",
    intro: "Welcome to the world of COCOON MIND, your tailor-made digital companion. Here, no pressure, no judgement: just curiosity, play, and the joy of exploring the world at your own pace.",
    constatLabel: "The Problem",
    constat: "Between grade stress, fear of failure and a rigid system, learning too often becomes a source of anxiety rather than joy. The natural spark of curiosity fades under the weight of school routine and daily pressure.",
    solutionLabel: "The Solution: COCOON MIND",
    solution: "COCOON MIND doesn't replace school — it accompanies it differently. Through gamification — video games in service of knowledge — we turn learning into a thrilling adventure.",
    nots: ["No school", "No teachers", "No homework", "No exams", "No diplomas", "No stress"],
    closing: "Just you and me. A virtual preceptor always available, adapted to your personality, turning every question into a game and every discovery into a victory.",
    joinLabel: "Join the adventure",
    joinCta: "Download the app",
    scan: "Scan to discover",
    print: "Print / PDF",
    copy: "Copy the message",
    copied: "Message copied!",
    back: "Back to home",
    socialTitle: "Social media card",
    socialLead: "A square visual ready to post on Instagram, TikTok or as a story.",
    download: "Download the card",
    storyLabel: "Story version (9:16)",
    waLabel: "WhatsApp",
    smsLabel: "SMS",
    shareImg: "Share image",
    msgLabel: "Your short note (optional)",
    msgPlaceholder: "e.g. I think you'll love this 🙂",
    msgIdeas: "Ideas",
    suggestions: [
      "I think you'll love this 🙂",
      "Learning without pressure — give it a try!",
      "Found something for you, take a look 👀",
    ],
    title2: "Your invitation to print & share",
    lead: "A ready-to-use flyer to pass on the essence of Cocoon Mind to your loved ones.",
    share: "✨ I invite you to discover COCOON MIND — the digital preceptor reinventing the joy of learning.\n\nNo school, no grades, no stress: just curiosity, play and the joy of exploring the world at your own pace.\n\nJoin the adventure 👉 " + SITE,
  },
  es: {
    eyebrow: "La Invitación",
    subtitle: "El preceptor digital que reinventa el placer de aprender.",
    hook: "¿Ganas de aprender sin presión? Descubre una nueva forma de crecer.",
    intro: "Bienvenido al universo de COCOON MIND, tu compañero digital a medida. Aquí no hay presión ni juicio: solo curiosidad, juego y el placer de descubrir el mundo a tu propio ritmo.",
    constatLabel: "La Constatación",
    constat: "Entre el estrés de las notas, el miedo al fracaso y la rigidez del sistema, aprender se convierte a menudo en una fuente de ansiedad en vez de placer. La chispa natural de la curiosidad se apaga bajo el peso de la rutina escolar y la presión diaria.",
    solutionLabel: "La Solución: COCOON MIND",
    solution: "COCOON MIND no reemplaza la escuela, la acompaña de otra manera. Gracias a la gamificación — el videojuego al servicio del saber — transformamos el aprendizaje en una aventura apasionante.",
    nots: ["Sin escuela", "Sin profesores", "Sin deberes", "Sin exámenes", "Sin diplomas", "Sin estrés"],
    closing: "Simplemente tú y yo. Un preceptor virtual siempre disponible, adaptado a tu personalidad, que convierte cada pregunta en un juego y cada descubrimiento en una victoria.",
    joinLabel: "Únete a la aventura",
    joinCta: "Descarga la aplicación",
    scan: "Escanea para descubrir",
    print: "Imprimir / PDF",
    copy: "Copiar el mensaje",
    copied: "¡Mensaje copiado!",
    back: "Volver al inicio",
    socialTitle: "Tarjeta para redes",
    socialLead: "Un visual cuadrado listo para publicar en Instagram, TikTok o en historia.",
    download: "Descargar la tarjeta",
    storyLabel: "Versión story (9:16)",
    waLabel: "WhatsApp",
    smsLabel: "SMS",
    shareImg: "Compartir imagen",
    msgLabel: "Tu pequeña frase (opcional)",
    msgPlaceholder: "Ej: creo que te va a encantar 🙂",
    msgIdeas: "Ideas",
    suggestions: [
      "Creo que te va a encantar 🙂",
      "Aprender sin presión existe — ¡pruébalo!",
      "Encontré algo para ti, échale un vistazo 👀",
    ],
    title2: "Tu invitación para imprimir y compartir",
    lead: "Un soporte listo para transmitir la esencia de Cocoon Mind a tus seres queridos.",
    share: "✨ Te invito a descubrir COCOON MIND — el preceptor digital que reinventa el placer de aprender.\n\nSin escuela, sin notas, sin estrés: solo curiosidad, juego y el placer de descubrir el mundo a tu ritmo.\n\nÚnete a la aventura 👉 " + SITE,
  },
  nl: {
    eyebrow: "De Uitnodiging",
    subtitle: "De digitale leermeester die het plezier in leren heruitvindt.",
    hook: "Zin om te leren zonder druk? Ontdek een nieuwe manier om te groeien.",
    intro: "Welkom in de wereld van COCOON MIND, je persoonlijke digitale metgezel. Hier geen druk, geen oordeel: alleen nieuwsgierigheid, spel en het plezier om de wereld op je eigen tempo te ontdekken.",
    constatLabel: "De Vaststelling",
    constat: "Tussen de stress van cijfers, de angst om te falen en een star systeem wordt leren te vaak een bron van angst in plaats van plezier. De natuurlijke vonk van nieuwsgierigheid dooft onder het gewicht van de schoolroutine en de dagelijkse druk.",
    solutionLabel: "De Oplossing: COCOON MIND",
    solution: "COCOON MIND vervangt de school niet, maar begeleidt haar anders. Dankzij gamificatie — de videogame ten dienste van kennis — maken we van leren een boeiend avontuur.",
    nots: ["Geen school", "Geen leraren", "Geen huiswerk", "Geen examens", "Geen diploma's", "Geen stress"],
    closing: "Gewoon jij en ik. Een virtuele leermeester die altijd beschikbaar is, aangepast aan jouw persoonlijkheid, die elke vraag in een spel verandert en elke ontdekking in een overwinning.",
    joinLabel: "Doe mee aan het avontuur",
    joinCta: "Download de app",
    scan: "Scan om te ontdekken",
    print: "Afdrukken / PDF",
    copy: "Bericht kopiëren",
    copied: "Bericht gekopieerd!",
    back: "Terug naar home",
    socialTitle: "Social media-kaart",
    socialLead: "Een vierkant beeld klaar om te posten op Instagram, TikTok of als story.",
    download: "Kaart downloaden",
    storyLabel: "Story-versie (9:16)",
    waLabel: "WhatsApp",
    smsLabel: "SMS",
    shareImg: "Afbeelding delen",
    msgLabel: "Jouw korte tekst (optioneel)",
    msgPlaceholder: "Bijv. ik denk dat je dit leuk vindt 🙂",
    msgIdeas: "Ideeën",
    suggestions: [
      "Ik denk dat je dit leuk vindt 🙂",
      "Leren zonder druk — probeer het!",
      "Ik vond iets voor jou, kijk eens 👀",
    ],
    title2: "Jouw uitnodiging om af te drukken & te delen",
    lead: "Een kant-en-klare flyer om de essentie van Cocoon Mind door te geven aan je dierbaren.",
    share: "✨ Ik nodig je uit om COCOON MIND te ontdekken — de digitale leermeester die het plezier in leren heruitvindt.\n\nGeen school, geen cijfers, geen stress: alleen nieuwsgierigheid, spel en het plezier om de wereld op je tempo te ontdekken.\n\nDoe mee aan het avontuur 👉 " + SITE,
  },
};

export default function Invitation() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "fr").slice(0, 2);
  const c = T[lang] || T.fr;
  const e = EVT[lang] || EVT.fr;
  const [copied, setCopied] = useState(false);
  const [refCode, setRefCode] = useState("");
  const [msg, setMsg] = useState("");
  const [debMsg, setDebMsg] = useState("");
  const [evTab, setEvTab] = useState("zones");
  const [confs, setConfs] = useState([]);
  const [debates, setDebates] = useState([]);
  const [selected, setSelected] = useState(null); // { type: zone|conf|debat, id, title }
  const [pending, setPending] = useState(null);
  const BE = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetch(`${BE}/api/referral/me`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d && d.code) setRefCode(d.code); })
      .catch(() => {});
  }, [BE]);

  // Load conferences + debates for the event-QR generator
  useEffect(() => {
    fetch(`${BE}/api/conference/current?lang=${lang}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d) return;
        const list = [];
        if (d.id && d.title) list.push({ id: d.id, title: d.title, month: d.month });
        (d.upcoming || []).forEach((u) => list.push({ id: u.id, title: u.title, month: u.month }));
        setConfs(list);
      })
      .catch(() => {});
    const i18nDeb = (t("arene.debates", { returnObjects: true }) || []).map((x) => ({ id: x.id, title: x.question }));
    fetch(`${BE}/api/debates/arena?lang=${lang}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        const monthly = d && d.items ? d.items.map((x) => ({ id: x.id, title: x.question })) : [];
        setDebates([...monthly, ...i18nDeb]);
      })
      .catch(() => setDebates(i18nDeb));
  }, [BE, lang]); // eslint-disable-line

  // Preselect an event when arriving from a card (?ev=zone|conf|debat&id=xxx)
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const ev = p.get("ev"), id = p.get("id");
    if (ev && id) {
      setEvTab(ev === "conf" ? "confs" : ev === "debat" ? "debates" : "zones");
      setPending({ ev, id });
    }
  }, []);
  useEffect(() => {
    if (!pending) return;
    if (pending.ev === "zone" && ZONE_IDS.includes(pending.id)) {
      setSelected({ type: "zone", id: pending.id, title: e.zones[pending.id] }); setPending(null);
    } else if (pending.ev === "conf") {
      const it = confs.find((x) => x.id === pending.id);
      if (it) { setSelected({ type: "conf", id: it.id, title: it.title }); setPending(null); }
    } else if (pending.ev === "debat") {
      const it = debates.find((x) => x.id === pending.id);
      if (it) { setSelected({ type: "debat", id: it.id, title: it.title }); setPending(null); }
    }
  }, [pending, confs, debates]); // eslint-disable-line

  useEffect(() => {
    const t2 = setTimeout(() => setDebMsg(msg.trim()), 450);
    return () => clearTimeout(t2);
  }, [msg]);

  const cardParams = (extra) => {
    const q = new URLSearchParams({ lang });
    if (refCode) q.set("ref", refCode);
    if (debMsg) q.set("msg", debMsg);
    if (extra) q.set(extra[0], extra[1]);
    return q.toString();
  };
  const cardUrl = `${BE}/api/brand/referral-card.png?${cardParams()}`;
  const storyUrl = `${BE}/api/brand/referral-card.png?${cardParams(["format", "story"])}`;
  const stickerUrl = `${BE}/api/brand/referral-card.png?${cardParams(["format", "sticker"])}`;
  const refLink = refCode ? `${SITE}?ref=${refCode}` : SITE;
  const shareText = (debMsg ? debMsg + "\n\n" : "") + c.share.replace(SITE, refLink);
  const waHref = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
  const smsHref = `sms:?body=${encodeURIComponent(shareText)}`;

  // ---- Event QR helpers ----
  const rq = refCode ? `ref=${encodeURIComponent(refCode)}` : "";
  const buildTo = (sel) => {
    if (!sel) return SITE;
    if (sel.type === "zone") return `${SITE}/${rq ? `?${rq}` : ""}#zone-${sel.id}`;
    if (sel.type === "conf") return `${SITE}/?${rq ? rq + "&" : ""}conf=${encodeURIComponent(sel.id)}#zone-agora`;
    return `${SITE}/?${rq ? rq + "&" : ""}debat=${encodeURIComponent(sel.id)}`;
  };
  const evTo = buildTo(selected);
  const evCardUrl = selected
    ? `${BE}/api/brand/referral-card.png?lang=${lang}${refCode ? `&ref=${encodeURIComponent(refCode)}` : ""}&event=${encodeURIComponent(selected.title)}&to=${encodeURIComponent(evTo)}`
    : "";
  const evStoryUrl = selected ? `${evCardUrl}&format=story` : "";
  const evShareText = (selected ? selected.title + "\n\n" : "") + evTo;
  const evWa = `https://wa.me/?text=${encodeURIComponent(evShareText)}`;
  const evSms = `sms:?body=${encodeURIComponent(evShareText)}`;
  const evShareImage = async () => {
    if (!evCardUrl) return;
    try {
      const res = await fetch(evCardUrl);
      const blob = await res.blob();
      const file = new File([blob], "cocoon-event.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text: evShareText });
        return;
      }
    } catch { /* fall through */ }
    if (navigator.share) {
      try { await navigator.share({ text: evShareText, url: evTo }); return; } catch { /* ignore */ }
    }
    window.open(evCardUrl, "_blank");
  };

  const shareImage = async () => {
    try {
      const res = await fetch(cardUrl);
      const blob = await res.blob();
      const file = new File([blob], "cocoon-invitation.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text: shareText });
        return;
      }
    } catch { /* fall through */ }
    if (navigator.share) {
      try { await navigator.share({ text: shareText, url: SITE }); return; } catch { /* ignore */ }
    }
    window.open(cardUrl, "_blank");
  };

  const copyMessage = async () => {
    try {
      await navigator.clipboard.writeText(c.share);
      setCopied(true);
      toast.success(c.copied);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <div className="invite-page relative min-h-screen bg-background text-foreground py-10 sm:py-16 px-4">
      <style>{`
        @media print {
          .invite-page { background: #0b0f1a !important; padding: 0 !important; }
          .no-print { display: none !important; }
          .invite-sheet { box-shadow: none !important; margin: 0 auto !important; max-width: 100% !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          @page { size: A4 portrait; margin: 12mm; }
        }
      `}</style>

      <div aria-hidden className="grain-overlay no-print" />
      <div aria-hidden className="amber-glow no-print" style={{ top: "-10%", left: "50%", transform: "translateX(-50%)", width: "800px", height: "600px" }} />

      {/* Toolbar */}
      <div className="no-print relative z-10 max-w-3xl mx-auto mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <Link to="/" data-testid="invite-back" className="inline-flex items-center gap-2 text-sm text-foreground/60 hover:text-primary transition-colors">
          <ArrowLeft size={16} /> {c.back}
        </Link>
        <div className="flex items-center gap-3">
          <button onClick={copyMessage} data-testid="invite-copy-btn" className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/[0.03] px-5 py-2.5 text-sm text-foreground/80 hover:border-primary/50 hover:text-primary transition-colors">
            {copied ? <Check size={15} /> : <Copy size={15} />} {copied ? c.copied : c.copy}
          </button>
          <button onClick={() => window.print()} data-testid="invite-print-btn" className="btn-frost-orange inline-flex items-center gap-2 px-6 py-2.5 rounded-full font-semibold text-sm">
            <Printer size={15} /> {c.print}
          </button>
        </div>
      </div>

      <div className="no-print relative z-10 max-w-3xl mx-auto mb-6 text-center">
        <h1 className="font-display text-3xl sm:text-4xl text-gradient-orange leading-tight">{c.title2}</h1>
        <p className="mt-2 text-sm sm:text-base text-foreground/55 max-w-xl mx-auto">{c.lead}</p>
      </div>

      {/* The printable flyer */}
      <motion.article
        initial={{ opacity: 0, y: 24, filter: "blur(8px)" }}
        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        data-testid="invite-sheet"
        className="invite-sheet relative z-10 max-w-3xl mx-auto rounded-[28px] overflow-hidden border border-primary/25"
        style={{ background: "linear-gradient(180deg, hsl(222 32% 7%), hsl(222 34% 5%))", boxShadow: "0 40px 90px hsl(32 90% 45% / 0.14)" }}
      >
        {/* Hero banner */}
        <div className="relative">
          <img src={HERO} alt="" className="w-full h-52 sm:h-64 object-cover" data-testid="invite-hero-image" />
          <div aria-hidden className="absolute inset-0" style={{ background: "linear-gradient(180deg, hsl(222 32% 6% / 0.35), hsl(222 32% 6%) 96%)" }} />
          <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8 flex items-center gap-3">
            <img src="/cocoon-logo-glow.png" alt="COCOON MIND" className="h-11 sm:h-14 w-auto" style={{ filter: "drop-shadow(0 0 12px hsl(28 96% 56% / 0.4))" }} />
            <span className="font-display text-2xl sm:text-3xl"><CocoonMark withMind /></span>
          </div>
        </div>

        <div className="px-7 sm:px-12 py-8 sm:py-10">
          <span className="inline-block text-[0.65rem] uppercase tracking-[0.3em] text-primary/75 border border-primary/25 rounded-full px-3 py-1 mb-4">{c.eyebrow}</span>
          <p className="font-display italic text-xl sm:text-2xl text-gradient-orange leading-snug mb-2">{c.subtitle}</p>
          <p className="text-base sm:text-lg text-foreground/85 leading-relaxed mb-1">{c.hook}</p>
          <p className="text-sm sm:text-[0.95rem] text-foreground/60 leading-relaxed">{c.intro}</p>

          {/* Le Constat */}
          <section className="mt-8" data-testid="invite-constat">
            <div className="flex items-center gap-2.5 mb-2">
              <span className="w-8 h-8 rounded-lg bg-primary/12 border border-primary/25 flex items-center justify-center text-primary flex-shrink-0"><Lightbulb size={16} /></span>
              <h2 className="font-display text-lg text-foreground/90">{c.constatLabel}</h2>
            </div>
            <p className="text-sm text-foreground/60 leading-relaxed pl-10.5">{c.constat}</p>
          </section>

          {/* La Solution */}
          <section className="mt-6" data-testid="invite-solution">
            <div className="flex items-center gap-2.5 mb-2">
              <span className="w-8 h-8 rounded-lg bg-primary/12 border border-primary/25 flex items-center justify-center text-primary flex-shrink-0"><Zap size={16} /></span>
              <h2 className="font-display text-lg text-foreground/90">{c.solutionLabel}</h2>
            </div>
            <p className="text-sm text-foreground/60 leading-relaxed">{c.solution}</p>
          </section>

          {/* Les "sans" */}
          <div className="mt-6 flex flex-wrap gap-2.5" data-testid="invite-nots">
            {c.nots.map((n, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 rounded-full border border-red-400/25 bg-red-400/[0.06] px-3.5 py-1.5 text-sm text-red-200/80">
                <Ban size={13} /> {n}
              </span>
            ))}
          </div>

          {/* Closing */}
          <div className="mt-7 rounded-2xl border border-primary/25 bg-primary/[0.05] p-5">
            <p className="flex items-start gap-2.5 font-display italic text-foreground/85 leading-relaxed">
              <Sparkles size={16} className="text-primary mt-1 flex-shrink-0" /> {c.closing}
            </p>
          </div>

          {/* Join + QR */}
          <div className="mt-8 pt-7 border-t border-white/10 flex flex-col sm:flex-row items-center gap-6">
            <div className="flex-shrink-0" data-testid="invite-qr">
              <div
                className="rounded-2xl p-[3px] shine-gold"
                style={{ backgroundImage: "linear-gradient(135deg, hsl(45 100% 70%), hsl(30 92% 48%) 50%, hsl(43 100% 70%))", boxShadow: "0 0 34px -6px hsl(40 96% 56% / 0.5)" }}
              >
                <div className="rounded-[14px] bg-white p-3">
                  <QRCodeSVG
                    value={refLink}
                    size={124}
                    level="H"
                    fgColor="#0b0f1a"
                    bgColor="#ffffff"
                    imageSettings={{ src: "/cocoon-logo-glow.png", height: 30, width: 30, excavate: true }}
                  />
                </div>
              </div>
            </div>
            <div className="flex-1 text-center sm:text-left">
              <p className="text-[0.65rem] uppercase tracking-[0.3em] text-primary/75 mb-1.5 flex items-center gap-2 justify-center sm:justify-start"><Smartphone size={14} /> {c.joinLabel}</p>
              <p className="text-sm text-foreground/55 mb-2">{c.scan} — {c.joinCta}</p>
              <a href={refLink} className="font-display text-xl sm:text-2xl text-gradient-orange break-all">www.cocoonmind.com</a>
            </div>
          </div>
        </div>
      </motion.article>

      {/* Carte réseaux sociaux */}
      <section className="no-print relative z-10 max-w-3xl mx-auto mt-14 text-center" data-testid="invite-social">
        <h2 className="font-display text-2xl sm:text-3xl text-gradient-orange">{c.socialTitle}</h2>
        <p className="mt-2 text-sm text-foreground/55 max-w-xl mx-auto">{c.socialLead}</p>
        <div className="mt-5 max-w-md mx-auto text-left">
          <label htmlFor="perso-msg" className="block text-xs text-foreground/50 mb-1.5">{c.msgLabel}</label>
          <textarea
            id="perso-msg" data-testid="invite-msg-input"
            value={msg} onChange={(e) => setMsg(e.target.value.slice(0, 160))}
            maxLength={160} rows={2} placeholder={c.msgPlaceholder}
            className="w-full rounded-xl bg-white/[0.04] border border-white/12 focus:border-primary/50 outline-none px-4 py-3 text-sm text-foreground/85 resize-none transition-colors"
          />
          <div className="text-right text-[11px] text-foreground/35 mt-1">{msg.length}/160</div>
          <div className="mt-2 flex flex-wrap gap-2" data-testid="invite-msg-suggestions">
            <span className="text-[11px] uppercase tracking-wider text-foreground/35 self-center mr-1">{c.msgIdeas}</span>
            {c.suggestions.map((s, i) => (
              <button
                key={i} type="button" onClick={() => setMsg(s)}
                data-testid={`invite-msg-suggestion-${i}`}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${msg === s ? "border-primary/60 bg-primary/15 text-primary" : "border-primary/25 text-primary/75 hover:bg-primary/10 hover:border-primary/50"}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-4 flex flex-col items-center gap-5">
          <img src={cardUrl} alt="COCOON MIND" loading="lazy" className="w-full max-w-xs rounded-2xl border border-primary/25 shadow-2xl" data-testid="invite-social-image" />
          <div className="flex flex-wrap items-center justify-center gap-3">
            <a href={cardUrl} download="cocoon-invitation.png" target="_blank" rel="noopener noreferrer" data-testid="invite-social-download" className="btn-frost-orange inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm">
              <Download size={16} /> {c.download}
            </a>
            <a href={storyUrl} download="cocoon-invitation-story.png" target="_blank" rel="noopener noreferrer" data-testid="invite-social-story" className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-primary/40 text-primary text-sm hover:bg-primary/10 transition-colors">
              <Smartphone size={15} /> {c.storyLabel}
            </a>
            <a href={stickerUrl} download="cocoon-sticker.png" target="_blank" rel="noopener noreferrer" data-testid="invite-social-sticker" className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-primary/40 text-primary text-sm hover:bg-primary/10 transition-colors">
              <Sticker size={15} /> {e.stickerLabel}
            </a>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <a href={waHref} target="_blank" rel="noopener noreferrer" data-testid="invite-share-whatsapp" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-emerald-400/40 text-emerald-300 text-sm hover:bg-emerald-400/10 transition-colors">
              <MessageCircle size={15} /> {c.waLabel}
            </a>
            <a href={smsHref} data-testid="invite-share-sms" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-sky-400/40 text-sky-300 text-sm hover:bg-sky-400/10 transition-colors">
              <MessageSquare size={15} /> {c.smsLabel}
            </a>
            <button onClick={shareImage} data-testid="invite-share-native" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-primary/40 text-primary text-sm hover:bg-primary/10 transition-colors">
              <Share2 size={15} /> {c.shareImg}
            </button>
          </div>
        </div>
      </section>

      {/* Générateur de QR d'événement */}
      <section className="no-print relative z-10 max-w-3xl mx-auto mt-16 text-center" data-testid="invite-event">
        <h2 className="font-display text-2xl sm:text-3xl text-gradient-orange inline-flex items-center gap-2.5 justify-center">
          <QrCode size={26} className="text-primary/80" /> {e.evtTitle}
        </h2>
        <p className="mt-2 text-sm text-foreground/55 max-w-xl mx-auto">{e.evtLead}</p>

        {/* Tabs */}
        <div className="mt-6 inline-flex flex-wrap justify-center gap-2 p-1.5 rounded-2xl border border-white/10 bg-white/[0.03]" data-testid="invite-event-tabs">
          {[
            { id: "zones", label: e.tabZones, icon: Compass },
            { id: "confs", label: e.tabConfs, icon: Mic },
            { id: "debates", label: e.tabDebates, icon: Swords },
          ].map((tb) => {
            const Icon = tb.icon;
            const activeTab = evTab === tb.id;
            return (
              <button
                key={tb.id} onClick={() => setEvTab(tb.id)}
                data-testid={`invite-event-tab-${tb.id}`}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm transition-colors ${activeTab ? "bg-primary/20 text-primary border border-primary/40" : "text-foreground/60 hover:text-primary border border-transparent"}`}
              >
                <Icon size={15} /> {tb.label}
              </button>
            );
          })}
        </div>

        {/* Item picker */}
        <p className="mt-6 text-xs uppercase tracking-[0.2em] text-foreground/40">{e.pick}</p>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="invite-event-items">
          {evTab === "zones" && ZONE_IDS.map((k) => {
            const on = selected && selected.type === "zone" && selected.id === k;
            return (
              <button key={k} data-testid={`invite-event-item-zone-${k}`}
                onClick={() => setSelected({ type: "zone", id: k, title: e.zones[k] })}
                className={`w-full min-h-[64px] flex items-center justify-center text-center text-sm px-4 py-3 rounded-xl border transition-colors ${on ? "border-primary/60 bg-primary/15 text-primary" : "border-primary/25 text-primary/75 hover:bg-primary/10 hover:border-primary/50"}`}>
                {e.zones[k]}
              </button>
            );
          })}
          {evTab === "confs" && (confs.length ? confs.map((it) => {
            const on = selected && selected.type === "conf" && selected.id === it.id;
            return (
              <button key={it.id} data-testid={`invite-event-item-conf-${it.id}`}
                onClick={() => setSelected({ type: "conf", id: it.id, title: it.title })}
                className={`w-full min-h-[64px] flex items-center justify-center text-center text-sm px-4 py-3 rounded-xl border transition-colors ${on ? "border-primary/60 bg-primary/15 text-primary" : "border-primary/25 text-primary/75 hover:bg-primary/10 hover:border-primary/50"}`}>
                {it.month ? `${it.month} — ` : ""}{it.title}
              </button>
            );
          }) : <span className="text-sm text-foreground/40">{e.empty}</span>)}
          {evTab === "debates" && (debates.length ? debates.map((it) => {
            const on = selected && selected.type === "debat" && selected.id === it.id;
            return (
              <button key={it.id} data-testid={`invite-event-item-debat-${it.id}`}
                onClick={() => setSelected({ type: "debat", id: it.id, title: it.title })}
                className={`w-full min-h-[64px] flex items-center justify-center text-center text-sm px-4 py-3 rounded-xl border transition-colors ${on ? "border-primary/60 bg-primary/15 text-primary" : "border-primary/25 text-primary/75 hover:bg-primary/10 hover:border-primary/50"}`}>
                {it.title}
              </button>
            );
          }) : <span className="text-sm text-foreground/40">{e.empty}</span>)}
        </div>

        {/* Selected event preview + actions */}
        {selected && (
          <div className="mt-8 flex flex-col items-center gap-5" data-testid="invite-event-preview">
            <img src={evCardUrl} alt={selected.title} loading="lazy" className="w-full max-w-xs rounded-2xl border border-primary/25 shadow-2xl" data-testid="invite-event-image" />
            <p className="text-xs text-foreground/45 max-w-sm">{e.evtScan}</p>
            <a href={evCardUrl} download="cocoon-event.png" target="_blank" rel="noopener noreferrer" data-testid="invite-event-download" className="btn-frost-orange inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm">
              <Download size={16} /> {e.evtDownload}
            </a>
            <a href={evStoryUrl} download="cocoon-event-story.png" target="_blank" rel="noopener noreferrer" data-testid="invite-event-story" className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-primary/40 text-primary text-sm hover:bg-primary/10 transition-colors">
              <Smartphone size={15} /> {c.storyLabel}
            </a>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <a href={evWa} target="_blank" rel="noopener noreferrer" data-testid="invite-event-whatsapp" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-emerald-400/40 text-emerald-300 text-sm hover:bg-emerald-400/10 transition-colors">
                <MessageCircle size={15} /> {c.waLabel}
              </a>
              <a href={evSms} data-testid="invite-event-sms" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-sky-400/40 text-sky-300 text-sm hover:bg-sky-400/10 transition-colors">
                <MessageSquare size={15} /> {c.smsLabel}
              </a>
              <button onClick={evShareImage} data-testid="invite-event-share" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-primary/40 text-primary text-sm hover:bg-primary/10 transition-colors">
                <Share2 size={15} /> {c.shareImg}
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
